import { useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';

import {
  useCustomerAcquisitionChannelsQuery,
  type SaveCustomerAcquisitionChannelInput,
  useSaveCustomerAcquisitionChannelMutation,
  useReorderCustomerAcquisitionChannelsMutation,
} from 'src/graphql/generated';

import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';

export default function AcquisitionChannelsConfig() {
  const { enqueueSnackbar } = useSnackbar();
  const [newChannelName, setNewChannelName] = useState('');
  const { data, refetch, loading } = useCustomerAcquisitionChannelsQuery();
  const [saveChannel, { loading: saving }] = useSaveCustomerAcquisitionChannelMutation();
  const [reorderChannels] = useReorderCustomerAcquisitionChannelsMutation();
  const channels = (data?.customerAcquisitionChannels?.edges ?? [])
    .flatMap((edge) => (edge?.node ? [edge.node] : []));

  const save = async (input: SaveCustomerAcquisitionChannelInput) => {
    try {
      await saveChannel({ variables: { input } });
      await refetch();
      setNewChannelName('');
    } catch (error) {
      enqueueSnackbar(error?.message || 'Não foi possível salvar o canal.', { variant: 'error' });
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= channels.length) return;
    const ordered = [...channels];
    [ordered[index], ordered[nextIndex]] = [ordered[nextIndex], ordered[index]];
    try {
      await reorderChannels({
        variables: { input: { channelIds: ordered.map((channel) => String(channel.pk)) } },
      });
      await refetch();
    } catch (error) {
      enqueueSnackbar(error?.message || 'Não foi possível ordenar os canais.', { variant: 'error' });
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Canais de aquisição</Typography>
          <Typography variant="body2" color="text.secondary">
            Defina as opções exibidas em “Como nos conheceu?” no cadastro do cliente.
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            fullWidth
            label="Novo canal"
            value={newChannelName}
            onChange={(event) => setNewChannelName(event.target.value)}
          />
          <Button
            variant="contained"
            disabled={!newChannelName.trim() || saving}
            onClick={() => save({ name: newChannelName })}
          >
            Adicionar
          </Button>
        </Stack>

        <Divider />

        <Stack divider={<Divider flexItem />}>
          {channels.map((channel, index) => (
            <Stack key={channel.pk} direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
              <TextField
                size="small"
                defaultValue={channel.name}
                disabled={saving}
                sx={{ flexGrow: 1 }}
                inputProps={{ 'aria-label': `Nome do canal ${channel.name}` }}
                onBlur={(event) => {
                  const name = event.target.value.trim();
                  if (name && name !== channel.name) {
                    save({ channelId: String(channel.pk), name, isActive: channel.isActive });
                  }
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={channel.isActive}
                    onChange={(_, isActive) =>
                      save({ channelId: String(channel.pk), name: channel.name, isActive })
                    }
                  />
                }
                label={channel.isActive ? 'Ativo' : 'Inativo'}
              />
              <Tooltip title="Mover para cima">
                <span>
                  <IconButton disabled={loading || index === 0} onClick={() => move(index, -1)}>
                    <Iconify icon="eva:arrow-upward-fill" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mover para baixo">
                <span>
                  <IconButton disabled={loading || index === channels.length - 1} onClick={() => move(index, 1)}>
                    <Iconify icon="eva:arrow-downward-fill" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}
